import React, { useEffect } from 'react';
import { Box, Typography, CircularProgress, Avatar } from '@mui/material';
import { useTheme } from '@mui/material';
import { useTranslation } from '../../hooks/useTranslation';
import StepperBar from '../common/Stepper';

const FipDiscoveryLoader = ({ fipIds = [], customDetails }) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const isDark =
		customDetails?.darkTheme === true || theme.palette.mode === 'dark';

	const displayFips = fipIds.slice(0, 5);
	const remainingCount = fipIds.length - 5;
	const fallbackUrl = 'https://finvu.in/cdn/finvu_bank_icon.png';

	// Prevent body scroll when loader is active
	useEffect(() => {
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = 'unset';
		};
	}, []);

	return (
		<>
			{/* Stepper */}
			<Box
				sx={{
					position: 'relative',
					zIndex: 10,
					marginTop: '-10px',
					'& > *': {
						paddingTop: '0px !important',
					},
				}}
			>
				<StepperBar customDetails={customDetails} />
			</Box>

			{/* Loader Content */}
			<Box
				sx={{
					position: 'fixed',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					zIndex: 1,
					backgroundColor: isDark ? '#161616' : theme.palette.primary.light,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					paddingTop: '160px',
					overflow: 'hidden',
				}}
			>
				{/* Circular Loader */}
				<CircularProgress
					size={60}
					thickness={4}
					sx={{
						color: theme.palette.primary.main,
						mb: '2rem',
					}}
				/>

				{/* Main Heading */}
				<Typography
					sx={{
						fontSize: '1.5rem',
						fontWeight: 600,
						color: isDark ? '#FFFFFF' : '#000000',
						mb: '0.125rem',
						mt: '1.5rem',
						textAlign: 'center',
					}}
				>
					{t('accountDiscovery.messages.findingSelectedAccounts')}
				</Typography>

				{/* Subheading */}
				<Typography
					sx={{
						fontSize: '1rem',
						color: isDark ? '#B0B0B0' : '#5F6368',
						mb: '0.5rem',
						textAlign: 'center',
					}}
				>
					{t('accountDiscovery.messages.mightTakeWhile')}
				</Typography>

				{/* FIP Logos */}
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						gap: 0,
					}}
				>
					{displayFips.map((fipId, index) => {
						const logoUrl = global.objectFormatFip?.[fipId]?.productIconUri;

						return (
							<Avatar
								key={fipId}
								src={logoUrl || fallbackUrl}
								alt={global.objectFormatFip?.[fipId]?.fipName || fipId}
								imgProps={{
									onError: (e) => {
										if (e.target.src !== fallbackUrl) {
											e.target.src = fallbackUrl;
										}
									},
								}}
								sx={{
									width: 30,
									height: 30,
									marginRight: index < displayFips.length - 1 ? '-6px' : '0',
									border: `.5px solid ${isDark ? '#7A8185' : '#D9D9D9'}`,
									backgroundColor: isDark ? '#2D2D2D' : 'white',
									zIndex: index + 1,
									'& .MuiAvatar-img': {
										objectFit: 'contain',
										padding: '2px',
									},
								}}
							/>
						);
					})}
					{remainingCount > 0 && (
						<Avatar
							sx={{
								width: 60,
								height: 30,
								marginRight: '0',
								marginLeft: '-6px',
								border: `.5px solid ${isDark ? '#7A8185' : 'grey'}`,
								backgroundColor: isDark ? '#2D2D2D' : 'white',
								color: isDark ? '#7A8185' : 'text.secondary',
								fontSize: '0.75rem',
								fontWeight: 600,
								zIndex: 6,
								borderRadius: '15px',
							}}
						>
							+{remainingCount} more
						</Avatar>
					)}
				</Box>
			</Box>
		</>
	);
};

export default FipDiscoveryLoader;
